import { Box, Popper, PopperProps } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Palette } from 'Palette';
import { StyledTypography } from './styles/styles';

interface SpeechBubblePortalProps {
  show: boolean;
  message: string;
  onClose?: () => void;
  targetRef?: React.RefObject<HTMLElement>;
  tone?: keyof typeof Palette;
  dismissable?: boolean;
  dismissDuration?: number;
  placement?: 'auto' | 'top' | 'bottom' | 'left' | 'right';
}

const SpeechBubblePortal: React.FC<SpeechBubblePortalProps> = ({
  show,
  message,
  onClose,
  targetRef,
  tone = 'tone1',
  dismissable = false,
  dismissDuration = 30000,
  placement = 'auto',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popperPlacement, setPopperPlacement] = useState<string | null>(null);
  const [arrowOffset, setArrowOffset] = useState<{
    side: 'left' | 'right';
    px: number;
  } | null>(null);

  const popperRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);

  // Popper modifiers
  const modifiers: PopperProps['modifiers'] = useMemo(
    () => [
      {
        name: 'offset',
        options: { offset: popperPlacement === 'bottom' ? [0, -6] : [0, 10] },
      },
      {
        name: 'arrow',
        enabled: true,
        options: { element: arrowRef.current, padding: 8 },
      },
      {
        name: 'updatePlacement',
        enabled: true,
        phase: 'write',
        fn: ({ state }) => {
          const next = state.placement;
          if (next !== popperPlacement) {
            setPopperPlacement(next);
          }
        },
      },
    ],
    [popperPlacement],
  );

  // Update anchor when shown
  useEffect(() => {
    setAnchorEl(show && targetRef?.current ? targetRef.current : null);
  }, [show, targetRef]);

  // Auto-dismiss
  useEffect(() => {
    if (!show || !onClose) return;
    const timer = setTimeout(onClose, dismissDuration);
    return () => clearTimeout(timer);
  }, [show, dismissDuration, onClose]);

  // Sync initial placement
  useEffect(() => {
    if (popperRef.current) {
      const attr = popperRef.current.getAttribute('data-popper-placement');
      if (attr) setPopperPlacement(attr);
    }
  }, [show, anchorEl]);

  // Recalculate arrow offset for top/bottom
  useEffect(() => {
    if (
      anchorEl &&
      popperRef.current &&
      popperPlacement &&
      (popperPlacement.startsWith('top') || popperPlacement.startsWith('bottom'))
    ) {
      const rect = anchorEl.getBoundingClientRect();
      const popRect = popperRef.current.getBoundingClientRect();
      const halfWidth = rect.width / 2;

      if (rect.left > popRect.left) {
        setArrowOffset({ side: 'right', px: halfWidth });
      } else {
        setArrowOffset({ side: 'left', px: halfWidth });
      }
    } else {
      setArrowOffset(null);
    }
  }, [anchorEl, popperPlacement]);

  if (!anchorEl) return null;

  return (
    <Popper
      open={show}
      anchorEl={anchorEl}
      placement={placement}
      modifiers={modifiers}
      ref={popperRef}
      style={{ zIndex: 1500 }}
    >
      <Box
        sx={{
          position: 'relative',
          bgcolor: Palette[tone].background,
          color: Palette[tone].text,
          p: '8px 12px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          maxWidth: '250px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        {/* Arrow */}
        <Box
          ref={arrowRef}
          sx={{
            position: 'absolute',
            width: 0,
            height: 0,
            // Bottom placement
            ...(popperPlacement?.startsWith('bottom') && {
              top: '-8px',
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderBottom: `8px solid ${Palette[tone].background}`,
              ...(arrowOffset
                ? { [arrowOffset.side]: `${arrowOffset.px}px` }
                : { left: '50%', transform: 'translateX(-50%)' }),
            }),
            // Top placement
            ...(popperPlacement?.startsWith('top') && {
              bottom: '-8px',
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: `8px solid ${Palette[tone].background}`,
              ...(arrowOffset
                ? { [arrowOffset.side]: `${arrowOffset.px}px` }
                : { left: '50%', transform: 'translateX(-50%)' }),
            }),
            // Right placement
            ...(popperPlacement?.startsWith('right') && {
              left: '-8px',
              top: '50%',
              transform: 'translateY(-50%)',
              borderTop: '8px solid transparent',
              borderBottom: '8px solid transparent',
              borderRight: `8px solid ${Palette[tone].background}`,
            }),
            // Left placement
            ...(popperPlacement?.startsWith('left') && {
              right: '-8px',
              top: '50%',
              transform: 'translateY(-50%)',
              borderTop: '8px solid transparent',
              borderBottom: '8px solid transparent',
              borderLeft: `8px solid ${Palette[tone].background}`,
            }),
          }}
        />

        {/* Message */}
        <StyledTypography
          variant="body2"
          component="span"
          tone={tone}
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.2,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            flexShrink: 1,
          }}
        >
          {message}
        </StyledTypography>

        {/* Dismiss button */}
        {dismissable && (
          <Box
            component="button"
            onClick={onClose}
            sx={{
              width: '1.25rem',
              height: '1.25rem',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: Palette[tone].text,
              fontSize: '0.875rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
              padding: 0,
              flexShrink: 0,
            }}
            aria-label="Dismiss"
          >
            ×
          </Box>
        )}
      </Box>
    </Popper>
  );
};

export default SpeechBubblePortal;
